/**
 * Convert a string to a URL-friendly slug
 * @param text - The text to slugify
 * @returns A URL-safe slug version of the text
 */
export const slugify = (text: string): string =>
  text
    .toString()
    .toLowerCase()
    .trim()
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove all non-word chars except hyphens
    .replace(/[^\w-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/--+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+/, '')
    .replace(/-+$/, '')

/**
 * UUID v4 pattern for validation
 */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Separator used to embed the organization ID in the invite slug.
 * Safe because slugify() collapses consecutive hyphens, so '--' can never
 * appear in the name-derived portion of the slug.
 */
const INVITE_SLUG_SEPARATOR = '--'

/**
 * Generate an organization invite URL
 * @param orgName - The organization name
 * @param orgId - The organization UUID
 * @returns The full invite URL with embedded org ID
 */
export const generateInviteUrl = (orgName: string, orgId: string): string => {
  const slug = slugify(orgName)
  return `https://attorneyshare.com/join/${slug}${INVITE_SLUG_SEPARATOR}${orgId}`
}

/**
 * Parse an invite slug (from the ref query param) to extract the organization ID.
 * Handles both new format (slug--orgId) and legacy format (slug only).
 * @param ref - The ref query parameter value
 * @returns The slug and organizationId (null if not present or invalid)
 */
export const parseInviteSlug = (
  ref: string
): { slug: string; organizationId: string | null } => {
  const separatorIndex = ref.lastIndexOf(INVITE_SLUG_SEPARATOR)

  if (separatorIndex === -1) {
    return { slug: ref, organizationId: null }
  }

  const slug = ref.substring(0, separatorIndex)
  const candidate = ref.substring(separatorIndex + INVITE_SLUG_SEPARATOR.length)

  if (UUID_REGEX.test(candidate)) {
    return { slug, organizationId: candidate }
  }

  return { slug: ref, organizationId: null }
}
